import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtEUR, fmtNum, fmtDateTime } from "@/lib/format";
import { Link2, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function parseTs(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * Anreichern-Dialog: schlägt für einen manuellen Trade passende OKX-Fills vor
 * (gleiches Asset, gleiche Seite, sortiert nach Zeit-Nähe zum Manuellen).
 * Nutzer wählt einen → POST /api/trades/{id}/enrich/{broker_pk}.
 */
export default function EnrichDialog({ open, onOpenChange, manualTrade, onEnriched }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/trades/broker-candidates`, { withCredentials: true });
      setCandidates(res.data || []);
    } catch {
      toast.error("Broker-Kandidaten konnten nicht geladen werden.");
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    load();
  }, [open, load]);

  const ranked = useMemo(() => {
    if (!manualTrade) return [];
    const mAsset = (manualTrade.asset || "").toLowerCase();
    const mSide = manualTrade.side;
    const mTs = parseTs(manualTrade.opened_at);
    return candidates
      .filter((c) => {
        const cAsset = (c.asset || "").toLowerCase();
        // Grober Asset-Match: exakt oder Substring in beide Richtungen (BTC-USDT vs BTC).
        const assetMatch =
          !mAsset || !cAsset || cAsset === mAsset || cAsset.includes(mAsset) || mAsset.includes(cAsset);
        const sideMatch = !mSide || !c.side || c.side === mSide;
        return assetMatch && sideMatch;
      })
      .map((c) => {
        const cTs = parseTs(c.opened_at);
        const distMin = mTs && cTs ? Math.abs(mTs - cTs) / 60000 : null;
        return { ...c, _distMin: distMin };
      })
      .sort((a, b) => {
        if (a._distMin == null && b._distMin == null) return 0;
        if (a._distMin == null) return 1;
        if (b._distMin == null) return -1;
        return a._distMin - b._distMin;
      })
      .slice(0, 20);
  }, [candidates, manualTrade]);

  const doEnrich = async () => {
    if (!selected || !manualTrade) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/trades/${manualTrade.id}/enrich/${selected.id}`,
        {},
        { withCredentials: true }
      );
      toast.success("Trade angereichert", {
        description: "Broker-Werte übernommen. Notizen und Reflexion bleiben erhalten.",
      });
      onEnriched?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Anreichern fehlgeschlagen", {
        description: e?.response?.data?.detail || "Bitte erneut versuchen.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="enrich-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Trade mit OKX-Daten anreichern
          </DialogTitle>
          <DialogDescription>
            Wähle den OKX-Fill, der zu deinem manuellen Trade gehört. Die Broker-Zahlen
            (Entry, Größe, Eröffnungszeit, Gebühren) überschreiben deine Eingaben —
            Notizen, Reflexion und Strategie-Zuordnung bleiben.
          </DialogDescription>
        </DialogHeader>

        {manualTrade && (
          <div
            className="rounded-xl border border-border bg-secondary/40 p-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-xs"
            data-testid="enrich-manual-summary"
          >
            <Cell label="Asset" value={manualTrade.asset || "—"} />
            <Cell label="Seite" value={<span className="capitalize">{manualTrade.side}</span>} />
            <Cell label="Eröffnet" value={manualTrade.opened_at ? fmtDateTime(manualTrade.opened_at) : "—"} />
            <Cell label="Entry" value={manualTrade.entry != null ? fmtNum(manualTrade.entry, 4) : "—"} />
            <Cell label="Größe" value={manualTrade.size != null ? fmtNum(manualTrade.size, 4) : "—"} />
            <Cell label="Gebühren" value={manualTrade.fees != null ? fmtEUR(manualTrade.fees) : "—"} />
          </div>
        )}

        <div className="flex-1 overflow-y-auto pt-2">
          {loading ? (
            <div className="flex items-center gap-2 justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              Broker-Kandidaten werden geladen…
            </div>
          ) : ranked.length === 0 ? (
            <div
              className="text-center py-14 border border-dashed border-border rounded-2xl"
              data-testid="enrich-empty"
            >
              <Sparkles className="mx-auto h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
              <div className="font-heading text-lg mt-3">Keine passenden OKX-Fills gefunden</div>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                Prüfe, ob die OKX-Verbindung aktiv ist und der letzte Sync durchgelaufen ist.
                Ein Match verlangt gleiches Asset und gleiche Seite (Long/Short).
              </p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="enrich-candidates">
              {ranked.map((c) => {
                const isSel = selected?.id === c.id;
                const distLabel =
                  c._distMin == null
                    ? "Zeit unbekannt"
                    : c._distMin < 1
                    ? "gleiche Minute"
                    : c._distMin < 60
                    ? `${Math.round(c._distMin)} Min. Abstand`
                    : c._distMin < 1440
                    ? `${(c._distMin / 60).toFixed(1)} h Abstand`
                    : `${Math.round(c._distMin / 1440)} T Abstand`;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      data-testid={`enrich-candidate-${c.id}`}
                      className={`w-full text-left rounded-xl border p-4 transition-colors ${
                        isSel
                          ? "border-primary/60 bg-primary/10"
                          : "border-border bg-card hover:bg-secondary/60"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <div className="font-heading">{c.asset}</div>
                          <Badge variant="secondary" className="rounded-full capitalize text-xs">
                            {c.side}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="rounded-full text-[10px] bg-secondary border border-border"
                          >
                            {c.market}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">{distLabel}</div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                        <Cell label="Eröffnet" value={c.opened_at ? fmtDateTime(c.opened_at) : "—"} />
                        <Cell label="Entry" value={c.entry != null ? fmtNum(c.entry, 4) : "—"} />
                        <Cell label="Größe" value={c.size != null ? fmtNum(c.size, 4) : "—"} />
                        <Cell label="Gebühren" value={c.fees != null ? fmtEUR(c.fees) : "—"} />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="pt-4 border-t border-border mt-2 flex-col-reverse sm:flex-row sm:justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="enrich-cancel-btn"
          >
            Abbrechen
          </Button>
          <Button
            onClick={doEnrich}
            disabled={!selected || saving}
            className="rounded-full h-11 px-5"
            data-testid="enrich-confirm-btn"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <Link2 className="mr-2 h-4 w-4" strokeWidth={1.5} />
            )}
            Werte übernehmen
            <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-mono truncate">{value}</div>
    </div>
  );
}
